import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { RouteMonitoring } from '../models';
import RouteMap from './RouteMap';
import { useRouteTrajectory } from '../hooks';

interface RouteDetailProps {
  routeDetail: RouteMonitoring;
  routeId: string;
}

function formatAddress(addr: RouteMonitoring['visits'][0]['address']): string {
  if (!addr) return '';
  const parts = [addr.streetName, addr.doorNumber, addr.apartmentNumber].filter(
    Boolean
  );
  return parts.join(' ');
}

const RouteDetail = ({ routeDetail, routeId }: RouteDetailProps) => {
  const navigate = useNavigate();
  const { trajectory } = useRouteTrajectory({ routeId });

  const visitsWithCoords = useMemo(() => {
    const sorted = [...routeDetail.visits].sort(
      (firstVisit, secondVisit) => firstVisit.order - secondVisit.order
    );
    const baseVisitId = sorted.find(
      (routeVisit) =>
        !(routeVisit.shippings && routeVisit.shippings.length > 0)
    )?.id;

    return sorted.map((visit) => {
      const useVisitCoords =
        visit.coordinatesChanged === true && visit.lat != null && visit.long != null;
      return {
        latitude: useVisitCoords ? String(visit.lat) : (visit.address?.lat ?? null),
        longitude: useVisitCoords ? String(visit.long) : (visit.address?.long ?? null),
        status: visit.status,
        order: visit.order,
        recipient: visit.recipient ?? visit.recipientName ?? undefined,
        address: formatAddress(visit.address),
        isBase: baseVisitId !== undefined && visit.id === baseVisitId,
        shippingNumber: visit.shippingNumber,
      };
    });
  }, [routeDetail.visits]);

  return (
    <div className='flex flex-col gap-4 w-full h-full'>
      <div className='flex items-center justify-between gap-4 flex-wrap'>
        <h2 className='text-xl font-bold text-gray-800'>
          {routeDetail.route.name || 'Ruta'}
        </h2>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => navigate('/business/monitoring')}
        >
          <ArrowBackIcon />
          Volver al listado
        </GlobalButton>
      </div>

      <div className='flex-1 min-h-[400px] rounded-lg overflow-hidden border border-gray-200 bg-white'>
        <RouteMap
          trajectory={trajectory}
          visits={visitsWithCoords}
          height='h-[calc(100vh-220px)]'
          liveDriverId={routeDetail.route.driver?.id}
          liveRouteStatus={routeDetail.route.status}
          driverName={
            routeDetail.route.driver
              ? `${routeDetail.route.driver.firstName} ${routeDetail.route.driver.lastName}`
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default RouteDetail;
